/**
 * Labstep
 */

import type { ProtocolValueActionBulkCreateItemsProps } from 'labstep-web/components/ProtocolValue/Action/BulkCreateItems/types';
import { EntityBatchProcessCreateContainer } from 'labstep-web/containers/Entity/BatchProcessCreate';
import { EntityCanCreateContainer } from 'labstep-web/containers/Entity/Create/Can';
import { useGridSmartTableProtocolConditionServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { Icon } from 'labstep-web/core/Icon';
import type { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';

export const ProtocolValueActionBulkCreateItems: React.FC<
  ProtocolValueActionBulkCreateItemsProps
> = ({
  variableTemplate,
  getProtocolConditions,
  experimentId,
  colDefId,
}) => {
  const bulkCreateSupported =
    variableTemplate?.resource ||
    variableTemplate?.resource_template?.custom_identifier_set;

  const gridSmartTableProtocolConditionService =
    useGridSmartTableProtocolConditionServiceFromContext();

  if (!bulkCreateSupported) {
    return null;
  }

  const protocolConditions = getProtocolConditions();
  const protocolConditionsWithItems = protocolConditions.filter(
    (c: ProtocolCondition) =>
      c.protocol_values_variable?.filter(
        (p: ProtocolValue) =>
          p.variable_template_guid === variableTemplate.guid &&
          p.resource_item_output,
      ).length > 0,
  );

  const itemsNotCreated =
    protocolConditions.length !== protocolConditionsWithItems.length;
  const handleCreateItems = (
    create: (items: any[], options: any) => void,
  ) => {
    const resourceParams = variableTemplate?.resource
      ? { resource_id: variableTemplate.resource.id }
      : {
          resource: {
            template_id: variableTemplate.resource_template?.guid,
          },
        };

    const items: any[] = [];

    const protocolConditions = getProtocolConditions();
    protocolConditions.forEach((condition: ProtocolCondition) => {
      const protocolValue = condition.protocol_values_variable?.find(
        (pv: ProtocolValue) =>
          pv.variable_template_guid === variableTemplate.guid,
      );

      if (protocolValue && protocolValue.resource_item_output) {
        return;
      }

      const protocolValueParams = protocolValue
        ? {
            protocol_value_origin_guid: protocolValue.guid,
          }
        : {
            protocol_value_origin: {
              protocol_condition_id: condition.guid,
              variable_template_id: variableTemplate.guid,
              experiment_id: experimentId,
            },
          };

      items.push({
        ...protocolValueParams,
        ...resourceParams,
        ...(protocolValue &&
          protocolValue.amount && { amount: protocolValue.amount }),
        ...(protocolValue &&
          protocolValue.unit && { unit: protocolValue.unit }),
      });
    });

    create(items, {
      onSuccess: () => {
        if (gridSmartTableProtocolConditionService && colDefId) {
          setTimeout(() => {
            gridSmartTableProtocolConditionService?.agGrid.api.refreshCells(
              {
                columns: [colDefId],
                force: true,
                suppressFlash: true,
              },
            );
          }, 50);
        }
      },
    });
  };

  return (
    <EntityCanCreateContainer entityName={ResourceItem.entityName}>
      {() => (
        <EntityBatchProcessCreateContainer
          entityName={ResourceItem.entityName}
          title="Creating items..."
          maxBatchSize={2}
        >
          {({ create, batchStatuses }) => {
            return (
              <Icon
                disabled={
                  batchStatuses &&
                  batchStatuses.some(
                    (batchStatuses) =>
                      batchStatuses.status?.isFetching,
                  )
                }
                color={itemsNotCreated ? 'yellow' : 'green'}
                name="check circle"
                data-testid="conditions_table_item_output"
                popup={{
                  content: itemsNotCreated
                    ? 'Create all items'
                    : 'All Items Created',
                  size: 'small',
                }}
                onClick={
                  itemsNotCreated
                    ? () => handleCreateItems(create)
                    : undefined
                }
              />
            );
          }}
        </EntityBatchProcessCreateContainer>
      )}
    </EntityCanCreateContainer>
  );
};
